import { Component, HostListener, OnInit } from '@angular/core';
import {
  ConfirmationService,
  ConfirmEventType,
  MenuItem,
  MessageService,
} from 'primeng/api';
import { Dialog } from 'primeng/dialog';
import { AppSettings } from '../Constant/AppSettings';
import { FeatureService } from '../services/feature/feature.service';
import { SubscriptionPlanService } from '../services/subscription-plan/subscription-plan.service';
import { UtilsService } from '../services/utils/utils.service';

@Component({
  selector: 'app-manage-subscription-plan',
  templateUrl: './manage-subscription-plan.component.html',
  styleUrls: ['./manage-subscription-plan.component.css'],
})
export class ManageSubscriptionPlanComponent implements OnInit {
  items: MenuItem[] = [{ label: 'Manage Subscription Plans' }];
  home: MenuItem = { icon: 'pi pi-home', routerLink: '/home' };

  public innerWidth: any;

  amountTypeOptions = [
    { label: 'Unlimited', value: 'UNLIMITED' },
    { label: 'Default', value: 'DEFAULT' },
    { label: 'Limited', value: 'LIMITED' },
    { label: 'Basic', value: 'BASIC' },
    { label: 'Standard', value: 'STANDARD' },
    { label: 'Contact Us (Call)', value: 'CALL' },
  ];

  // ─── Plans ───
  plans: any[] = [];
  isLoadingPlans = false;
  disabledPlan = false;
  displayAddPlanModal = false;
  displayEditPlanModal = false;
  activeFeatures: any[] = [];

  featureOptions: { label: string; value: number }[] = [];

  planId: any = null;
  planName = '';
  planDescription = '';
  planMonthlyPrice: number | null = null;
  planYearlyPrice: number | null = null;
  planSetupFee: number | null = null;
  planTax: number | null = null;
  planInfo = '';
  planFeatureIds: number[] = [];

  // ─── Features ───
  features: any[] = [];
  isLoadingFeatures = false;
  disabledFeature = false;
  displayAddFeatureModal = false;
  displayEditFeatureModal = false;

  featureId: any = null;
  featureName = '';
  featureDescription = '';
  featureAmount: number | null = null;
  featureAmountType = 'DEFAULT';

  constructor(
    public utils: UtilsService,
    private subscriptionPlanService: SubscriptionPlanService,
    private featureService: FeatureService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.innerWidth = window.innerWidth;
    this.listFeatures();
    this.listPlans();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.innerWidth = window.innerWidth;
  }

  // ─── Plans: list/add/edit/remove ───

  listPlans() {
    this.plans = [];
    this.isLoadingPlans = true;
    this.subscriptionPlanService.listPlans().subscribe(
      (response) => {
        const json = JSON.parse(JSON.stringify(response));
        this.plans = json.data || [];
        this.isLoadingPlans = false;
      },
      (error) => {
        console.log(error);
        this.isLoadingPlans = false;
        this.showErrorToastMsg(AppSettings.ERROR_MSG);
      }
    );
  }

  private buildPlanPayload(): any {
    return {
      name: this.planName,
      description: this.planDescription,
      monthlyPrice: this.planMonthlyPrice,
      yearlyPrice: this.planYearlyPrice,
      setupFee: this.planSetupFee,
      tax: this.planTax,
      info: this.planInfo,
      features: (this.planFeatureIds || []).map((id) => ({ id })),
    };
  }

  private validatePlanForm(): boolean {
    if (!this.planName.trim()) {
      this.showErrorMessage('Plan Name is required.');
      return false;
    }
    if (this.planMonthlyPrice === null || this.planMonthlyPrice === undefined) {
      this.showErrorMessage('Monthly Price is required.');
      return false;
    }
    if (this.planYearlyPrice === null || this.planYearlyPrice === undefined) {
      this.showErrorMessage('Yearly Price is required.');
      return false;
    }
    return true;
  }

  addPlan() {
    this.showProgressBar();
    if (!this.validatePlanForm()) {
      this.hideProgressBar();
      return;
    }
    this.subscriptionPlanService.addPlan(this.buildPlanPayload()).subscribe(
      (response) => {
        const json = JSON.parse(JSON.stringify(response));
        if (json.response?.status === 'SUCCESS') {
          this.showHidePlanModalDialog('add_plan');
          this.showSuccessMessage(json.response.status, json.response.message);
          this.clearPlanFormData();
          this.listPlans();
        } else {
          this.showErrorMessage(json.response?.displayMessage || 'An error occurred.');
        }
        this.hideProgressBar();
      },
      (error) => {
        console.log(error);
        this.showErrorMessage(AppSettings.ERROR_MSG);
        this.hideProgressBar();
      }
    );
  }

  editPlan() {
    this.showProgressBar();
    if (!this.validatePlanForm()) {
      this.hideProgressBar();
      return;
    }
    this.subscriptionPlanService.editPlan(this.buildPlanPayload(), this.planId).subscribe(
      (response) => {
        const json = JSON.parse(JSON.stringify(response));
        if (json.response?.status === 'SUCCESS') {
          this.showHidePlanModalDialog('edit_plan');
          this.showSuccessMessage(json.response.status, json.response.message);
          this.listPlans();
        } else {
          this.showErrorMessage(json.response?.displayMessage || 'An error occurred.');
        }
        this.hideProgressBar();
      },
      (error) => {
        console.log(error);
        this.showErrorMessage(AppSettings.ERROR_MSG);
        this.hideProgressBar();
      }
    );
  }

  openEditPlanDialog(plan: any) {
    this.clearPlanFormData();
    this.planId = plan.id;
    this.planName = plan.name;
    this.planDescription = plan.description;
    this.planMonthlyPrice = plan.monthlyPrice;
    this.planYearlyPrice = plan.yearlyPrice;
    this.planSetupFee = plan.setupFee;
    this.planTax = plan.tax;
    this.planInfo = plan.info;
    this.planFeatureIds = (plan.features || []).map((f: any) => f.id);
    this.showHidePlanModalDialog('edit_plan');
  }

  confirmDeletePlanDialog(plan: any) {
    this.messageService.clear();
    this.confirmationService.confirm({
      message: `Are you sure you want to remove subscription plan "${plan.name}"?`,
      header: 'Delete Confirmation',
      icon: 'pi pi-trash',
      accept: () => {
        this.removePlan(plan);
      },
      reject: (type: any) => {
        if (type === ConfirmEventType.CANCEL) {
          this.messageService.add({
            key: 'br',
            severity: 'warn',
            summary: 'Cancelled',
            detail: 'You have cancelled',
          });
        }
      },
    });
  }

  removePlan(plan: any) {
    this.subscriptionPlanService.removePlan(plan.id).subscribe(
      (response) => {
        const json = JSON.parse(JSON.stringify(response));
        if (json.response?.status === 'SUCCESS') {
          this.showSuccessMessage(json.response.status, json.response.message);
          this.listPlans();
        } else {
          this.showErrorToastMsg(json.response?.message || 'An error occurred.');
        }
      },
      (error) => {
        console.log(error);
        this.showErrorToastMsg(AppSettings.ERROR_MSG);
      }
    );
  }

  getFeatureCountLabel(plan: any): string {
    const features = plan.features || [];
    if (features.length === 0) return 'None';
    return features.slice(0, 2).map((f: any) => f.name).join(', ');
  }

  showFeaturePopup(event: Event, plan: any, overlay: any) {
    this.activeFeatures = plan.features || [];
    overlay.toggle(event);
  }

  clearPlanFormData() {
    this.messageService.clear('planErrMsg');
    this.planId = null;
    this.planName = '';
    this.planDescription = '';
    this.planMonthlyPrice = null;
    this.planYearlyPrice = null;
    this.planSetupFee = null;
    this.planTax = null;
    this.planInfo = '';
    this.planFeatureIds = [];
  }

  showHidePlanModalDialog(type: string) {
    switch (type) {
      case 'add_plan':
        this.clearPlanFormData();
        this.displayAddPlanModal = !this.displayAddPlanModal;
        break;
      case 'edit_plan':
        this.displayEditPlanModal = !this.displayEditPlanModal;
        break;
    }
  }

  // ─── Features: list/add/edit/remove ───

  listFeatures() {
    this.features = [];
    this.isLoadingFeatures = true;
    this.featureService.listFeatures().subscribe(
      (response) => {
        const json = JSON.parse(JSON.stringify(response));
        this.features = json.data || [];
        this.featureOptions = this.features.map((f) => ({
          label: `${f.name} (${f.amountType}${f.amount ? ': ' + f.amount : ''})`,
          value: f.id,
        }));
        this.isLoadingFeatures = false;
      },
      (error) => {
        console.log(error);
        this.isLoadingFeatures = false;
        this.showErrorToastMsg(AppSettings.ERROR_MSG);
      }
    );
  }

  private buildFeaturePayload(): any {
    return {
      name: this.featureName,
      description: this.featureDescription,
      amount: this.featureAmount,
      amountType: this.featureAmountType,
    };
  }

  private validateFeatureForm(): boolean {
    if (!this.featureName.trim()) {
      this.showFeatureErrorMessage('Feature Name is required.');
      return false;
    }
    return true;
  }

  addFeature() {
    this.showFeatureProgressBar();
    if (!this.validateFeatureForm()) {
      this.hideFeatureProgressBar();
      return;
    }
    this.featureService.addFeature(this.buildFeaturePayload()).subscribe(
      (response) => {
        const json = JSON.parse(JSON.stringify(response));
        if (json.response?.status === 'SUCCESS') {
          this.showHideFeatureModalDialog('add_feature');
          this.showSuccessMessage(json.response.status, json.response.message);
          this.clearFeatureFormData();
          this.listFeatures();
        } else {
          this.showFeatureErrorMessage(json.response?.displayMessage || 'An error occurred.');
        }
        this.hideFeatureProgressBar();
      },
      (error) => {
        console.log(error);
        this.showFeatureErrorMessage(AppSettings.ERROR_MSG);
        this.hideFeatureProgressBar();
      }
    );
  }

  editFeature() {
    this.showFeatureProgressBar();
    if (!this.validateFeatureForm()) {
      this.hideFeatureProgressBar();
      return;
    }
    this.featureService.editFeature(this.buildFeaturePayload(), this.featureId).subscribe(
      (response) => {
        const json = JSON.parse(JSON.stringify(response));
        if (json.response?.status === 'SUCCESS') {
          this.showHideFeatureModalDialog('edit_feature');
          this.showSuccessMessage(json.response.status, json.response.message);
          this.listFeatures();
        } else {
          this.showFeatureErrorMessage(json.response?.displayMessage || 'An error occurred.');
        }
        this.hideFeatureProgressBar();
      },
      (error) => {
        console.log(error);
        this.showFeatureErrorMessage(AppSettings.ERROR_MSG);
        this.hideFeatureProgressBar();
      }
    );
  }

  openEditFeatureDialog(feature: any) {
    this.clearFeatureFormData();
    this.featureId = feature.id;
    this.featureName = feature.name;
    this.featureDescription = feature.description;
    this.featureAmount = feature.amount;
    this.featureAmountType = feature.amountType || 'DEFAULT';
    this.showHideFeatureModalDialog('edit_feature');
  }

  confirmDeleteFeatureDialog(feature: any) {
    this.messageService.clear();
    this.confirmationService.confirm({
      message: `Are you sure you want to remove feature "${feature.name}"?`,
      header: 'Delete Confirmation',
      icon: 'pi pi-trash',
      accept: () => {
        this.removeFeature(feature);
      },
      reject: (type: any) => {
        if (type === ConfirmEventType.CANCEL) {
          this.messageService.add({
            key: 'br',
            severity: 'warn',
            summary: 'Cancelled',
            detail: 'You have cancelled',
          });
        }
      },
    });
  }

  removeFeature(feature: any) {
    this.featureService.removeFeature(feature.id).subscribe(
      (response) => {
        const json = JSON.parse(JSON.stringify(response));
        if (json.response?.status === 'SUCCESS') {
          this.showSuccessMessage(json.response.status, json.response.message);
          this.listFeatures();
        } else {
          this.showErrorToastMsg(json.response?.message || 'An error occurred.');
        }
      },
      (error) => {
        console.log(error);
        this.showErrorToastMsg(AppSettings.ERROR_MSG);
      }
    );
  }

  clearFeatureFormData() {
    this.messageService.clear('featureErrMsg');
    this.featureId = null;
    this.featureName = '';
    this.featureDescription = '';
    this.featureAmount = null;
    this.featureAmountType = 'DEFAULT';
  }

  showHideFeatureModalDialog(type: string) {
    switch (type) {
      case 'add_feature':
        this.clearFeatureFormData();
        this.displayAddFeatureModal = !this.displayAddFeatureModal;
        break;
      case 'edit_feature':
        this.displayEditFeatureModal = !this.displayEditFeatureModal;
        break;
    }
  }

  // ─── Shared helpers ───

  showDialogMaximized(event: any, dialog: Dialog) {
    if (this.innerWidth <= 640) {
      dialog.maximized = true;
    }
  }

  showProgressBar() {
    this.messageService.clear();
    this.isLoadingPlans = true;
    this.disabledPlan = true;
  }

  hideProgressBar() {
    this.isLoadingPlans = false;
    this.disabledPlan = false;
  }

  showFeatureProgressBar() {
    this.messageService.clear();
    this.isLoadingFeatures = true;
    this.disabledFeature = true;
  }

  hideFeatureProgressBar() {
    this.isLoadingFeatures = false;
    this.disabledFeature = false;
  }

  showErrorMessage(msg: string) {
    this.messageService.add({ key: 'planErrMsg', severity: 'error', detail: msg });
  }

  showFeatureErrorMessage(msg: string) {
    this.messageService.add({ key: 'featureErrMsg', severity: 'error', detail: msg });
  }

  showErrorToastMsg(msg: string) {
    this.messageService.add({ key: 'br', severity: 'error', detail: msg });
  }

  showSuccessMessage(status: string, msg: string) {
    this.messageService.add({ key: 'br', severity: 'success', summary: status, detail: msg });
  }

  get state(): string {
    return this.utils.state;
  }
}
